import Link from 'next/link';
import { MouseEventHandler, useMemo } from 'react';
import clsx from 'clsx';

import { CommentV2 as CommentV2Type } from '~/lib/discussions/types';
import useUser from '~/lib/user';
import { useCommentV2 } from '~/lib/discussions/useCommentV2';

type ActionType =
  | 'approve'
  | 'unapprove'
  | 'deny'
  | 'undeny'
  | 'resolve'
  | 'unresolve'
  | 'block_user'
  | 'unblock_user'
  | 'approve_all_posts'
  | 'unapprove_all_posts';

type Action = {
  type: ActionType;
  label: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
};

export function ActionsV2({
  href,
  comment,
  mutateComment,
  moderator = false,
}: {
  href?: string; // let's not include 'Link' on Q/R pages
  comment: CommentV2Type;
  mutateComment: () => void;
  moderator?: boolean;
}) {
  const { user } = useUser();

  const {
    approve,
    unapprove,
    approveAllPosts,
    disableApproveAllPosts,
    blockUser,
    unblockUser,
    deny,
    undeny,
    resolve,
    unresolve,
    disableVoteBtn,
  } = useCommentV2(comment, mutateComment);

  const actions = useMemo(() => {
    const actions: Action[] = [];
    const isStaff = user && user.is_staff;

    if (moderator || isStaff) {
      actions.push({
        type: comment.resolved ? 'unresolve' : 'resolve',
        label: comment.resolved ? 'Mark as Unresolved' : 'Mark as Resolved',
        onClick: comment.resolved ? unresolve : resolve,
      });

      if (
        typeof comment.approved === 'undefined' ||
        comment.approved === null
      ) {
        actions.push({
          type: 'approve',
          label: 'Approve',
          onClick: approve,
        });
      }
    }

    // staff only controls
    if (isStaff) {
      if (comment.approved) {
        actions.push({
          type: 'unapprove',
          label: 'Unapprove',
          onClick: unapprove,
        });
      }

      // deny vs. undeny
      const canDeny =
        comment.approved ||
        typeof comment.approved === 'undefined' ||
        comment.approved === null;

      actions.push({
        type: canDeny ? 'deny' : 'undeny',
        label: canDeny ? 'Deny' : 'Undeny',
        onClick: canDeny ? deny : undeny,
      });

      actions.push({
        type: comment.user.comment_whitelist
          ? 'unapprove_all_posts'
          : 'approve_all_posts',
        label: comment.user.comment_whitelist
          ? 'Disable approve all posts'
          : 'Approve all posts',
        onClick: comment.user.comment_whitelist
          ? disableApproveAllPosts
          : approveAllPosts,
      });

      actions.push({
        type: comment.user.comment_blacklist ? 'unblock_user' : 'block_user',
        label: comment.user.comment_blacklist ? 'Unblock user' : 'Block user',
        onClick: comment.user.comment_blacklist ? unblockUser : blockUser,
      });
    }

    return actions;
  }, [
    user,
    moderator,
    comment,
    unresolve,
    resolve,
    approve,
    deny,
    undeny,
    disableApproveAllPosts,
    approveAllPosts,
    unblockUser,
    blockUser,
    unapprove,
  ]);

  return (
    <ul className="mt-4 flex flex-wrap justify-end gap-x-2 text-xs">
      {href && (
        <li>
          <Link
            href={`${href}`}
            className="text-blue-600 hover:underline"
            target="_blank"
          >
            Link
          </Link>
        </li>
      )}
      {actions.map((action, i) => (
        <li
          key={action.type}
          className={clsx({
            "before:mr-2 before:text-gray-400 before:content-['|']":
              i > 0 || href,
          })}
        >
          <button
            type="button"
            className="whitespace-nowrap text-blue-600 hover:underline"
            onClick={action.onClick}
            disabled={disableVoteBtn}
          >
            {action.label}
          </button>
        </li>
      ))}
    </ul>
  );
}
